import React, { useMemo } from 'react';

import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import dayjs from '~/timeInit';

import { Box, Paper, Stack, Typography, useTheme } from '@mui/material';

import { TimelinePoint } from './departureInventory';
import { formatAmount } from './formatAmount';

interface Props {
  timeline: Array<TimelinePoint>;
  currency: string;
}

/**
 * One series, so the title names it and no legend is needed. The hue is the
 * first categorical slot, which passes the contrast and chroma checks against
 * both the light and dark chart surfaces.
 */
const SERIES = { light: '#2a78d6', dark: '#3987e5' };

const AXIS_INK = '#898781';
const GRID = { light: '#e1e0d9', dark: '#2c2c2a' };

/** Rates run to eight figures in VND, which no axis can carry in full. */
const compact = new Intl.NumberFormat('en-AU', { notation: 'compact', maximumFractionDigits: 1 });

interface ChartPoint {
  time: number;
  totalCost: number;
  unitsAfter: number;
  totalCostDelta: number | null;
}

function TrendTooltip({
  active,
  payload,
  currency,
}: {
  active?: boolean;
  payload?: Array<{ payload: ChartPoint }>;
  currency: string;
}) {
  const point = payload?.[0]?.payload;

  if (!active || !point) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={{ px: 1.5, py: 1 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {dayjs(point.time).format('D MMM YYYY')}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {formatAmount(point.totalCost)} {currency}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {point.unitsAfter} traveller{point.unitsAfter === 1 ? '' : 's'} booked
      </Typography>
      {point.totalCostDelta !== null && point.totalCostDelta !== 0 && (
        <Typography
          variant="caption"
          display="block"
          sx={{ color: point.totalCostDelta < 0 ? 'success.main' : 'text.secondary' }}
        >
          {point.totalCostDelta < 0 ? '↓' : '↑'} {formatAmount(Math.abs(point.totalCostDelta))} {currency} on this
          change
        </Typography>
      )}
    </Paper>
  );
}

/**
 * What the departure has owed the supplier over time.
 *
 * The total rather than a per-traveller rate: options on one departure can be
 * priced completely differently, so there is no single per-traveller figure to
 * plot - but there is always one amount owed.
 *
 * Stepped rather than smoothed: the amount holds flat between movements and
 * jumps at one, and a sloped line would draw totals that were never owed.
 */
export default function CostPriceTrendChart({ timeline, currency }: Props) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const data = useMemo<Array<ChartPoint>>(
    () =>
      timeline
        .filter((point) => point.totalCost !== null)
        .map((point) => ({
          time: dayjs(point.occurredAt).valueOf(),
          totalCost: point.totalCost,
          unitsAfter: point.unitsAfter,
          totalCostDelta: point.totalCostDelta,
        })),
    [timeline],
  );

  if (data.length < 2) {
    return (
      <Typography variant="body2" color="text.secondary">
        Not enough movements to show a trend yet.
      </Typography>
    );
  }

  const stroke = isDark ? SERIES.dark : SERIES.light;

  return (
    <Stack spacing={0.5}>
      <Box sx={{ width: '100%', height: 240 }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 8, right: 16, bottom: 4, left: 8 }}>
            <CartesianGrid stroke={isDark ? GRID.dark : GRID.light} vertical={false} />
            <XAxis
              dataKey="time"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(value) => dayjs(value).format('D MMM')}
              tick={{ fill: AXIS_INK, fontSize: 12 }}
              stroke={isDark ? GRID.dark : GRID.light}
            />
            <YAxis
              tickFormatter={(value) => compact.format(value)}
              tick={{ fill: AXIS_INK, fontSize: 12 }}
              stroke={isDark ? GRID.dark : GRID.light}
              width={56}
              domain={['auto', 'auto']}
            />
            <Tooltip
              content={<TrendTooltip currency={currency} />}
              cursor={{ stroke: AXIS_INK, strokeDasharray: '3 3' }}
            />
            <Line
              type="stepAfter"
              dataKey="totalCost"
              stroke={stroke}
              strokeWidth={2}
              // Uniform dots: every step in the line already is a band change,
              // so sizing them by it would encode the same thing twice.
              dot={{ r: 4, fill: stroke, stroke: theme.palette.background.paper, strokeWidth: 2 }}
              activeDot={{ r: 6, fill: stroke, stroke: theme.palette.background.paper, strokeWidth: 2 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Stack>
  );
}
